/**
 * Blue Speed Construction — site photography registry.
 *
 * Single source of truth for every project photo used on the site, mirroring the
 * way `company-content.js` owns the wording. Views import from here rather than
 * pointing `src="@/assets/..."` at files directly, so a photo can be recaptioned
 * or swapped in one place.
 *
 * The files in `@/assets/work/` are web-optimised copies (max 1600px on the long
 * edge, JPEG) of the originals the client supplied in
 * `@/assets/Company Website Photo/`. The originals are left untouched.
 *
 * Captions follow the client's own folder split: photos filed under
 * "Showcase-Various Service With Location Info" name the building; photos filed
 * under "…-No need indicate location" do not.
 *
 * Deliberately not published (kept in the originals folder only):
 *   - Spalling Concrete Repair-2 — hand holding a fallen slab, unclear subject.
 *   - Work for Location with limit access 2 — burnt-in "5/5/26 Choa Chu Kang"
 *     camera stamp and low resolution.
 *   - Cleaning-Univeral Studio- — burnt-in "Nov 27, 2023, 20:38" camera stamp.
 */

export const ROPE_ACCESS = "rope-access";
export const BUILDING_CIVIL = "building-civil";

export const workCategories = [
  { key: "all", label: "All work" },
  { key: ROPE_ACCESS, label: "Rope access" },
  { key: BUILDING_CIVIL, label: "Building & civil" },
];

/**
 * `service` matches the wording used in company-content.js so the gallery reads
 * as the same vocabulary as the services pages.
 */
export const workPhotos = [
  /* ---------- Rope Access Services Division ---------- */

  {
    id: "cleaning-genting-hotel",
    src: require("@/assets/work/rope-cleaning-genting-hotel.jpg"),
    category: ROPE_ACCESS,
    service: "Façade cleaning",
    location: "Genting Hotel Jurong",
    caption:
      "A full rope team working a glazed elevation from the roof down, with no scaffolding at ground level.",
    alt: "Five Blue Speed rope access technicians descending the glass façade of Genting Hotel Jurong",
    orientation: "portrait",
  },
  {
    id: "cleaning-mbfc-tower-1",
    src: require("@/assets/work/rope-cleaning-mbfc-tower-1.jpg"),
    category: ROPE_ACCESS,
    service: "Glass panel cleaning",
    location: "Marina Bay Financial Centre Tower 1",
    caption:
      "Glass panel cleaning across a high-rise commercial tower that stayed in full operation throughout.",
    alt: "Four rope access technicians cleaning glass panels on Marina Bay Financial Centre Tower 1",
    orientation: "portrait",
  },
  {
    id: "roof-painting-marriott",
    src: require("@/assets/work/rope-roof-painting-marriott.jpg"),
    category: ROPE_ACCESS,
    service: "Roof painting",
    location: "Singapore Marriott Tang Plaza Hotel",
    caption:
      "Painting works on a pitched landmark roof and finial, reached entirely on rope.",
    alt: "Technicians on ropes painting the red finial above the pagoda roof of the Marriott hotel",
    orientation: "portrait",
  },
  {
    id: "facade-cladding",
    src: require("@/assets/work/rope-facade-cladding.jpg"),
    category: ROPE_ACCESS,
    service: "Cladding and glazing panel replacement",
    caption:
      "Cladding works across a large space-frame roof — the technicians give the scale of the structure.",
    alt: "Aerial view of a white geometric space-frame roof with rope access technicians spread across it",
    orientation: "landscape",
  },
  {
    id: "limited-access-atrium",
    src: require("@/assets/work/rope-limited-access-atrium.jpg"),
    category: ROPE_ACCESS,
    service: "Work in locations with limited access",
    caption:
      "Working inside a glazed atrium roof, where staging or a gondola would not be practical.",
    alt: "Rope access technicians suspended inside the steel frame of a glazed atrium roof",
    orientation: "landscape",
  },
  {
    id: "painting-farrer-park",
    src: require("@/assets/work/rope-painting-farrer-park.jpg"),
    category: ROPE_ACCESS,
    service: "External painting works",
    location: "Farrer Park Medical Centre",
    caption:
      "External wall painting worked in vertical drops so the elevation is finished band by band.",
    alt: "Blue Speed technicians painting a white external wall from ropes under a dark sky",
    orientation: "portrait",
  },
  {
    id: "cleaning-farrer-sunshade",
    src: require("@/assets/work/rope-cleaning-farrer-sunshade.jpg"),
    category: ROPE_ACCESS,
    service: "Sunshade cleaning",
    location: "Farrer Park Medical Centre",
    caption:
      "Cleaning fixed sunshades and the glazing behind them — awkward geometry for conventional access.",
    alt: "Rope access technicians cleaning angled sunshades on a colourful medical centre façade",
    orientation: "portrait",
  },
  {
    id: "cleaning-farrer-glass",
    src: require("@/assets/work/rope-cleaning-farrer-glass.jpg"),
    category: ROPE_ACCESS,
    service: "Window and skylight cleaning",
    location: "Farrer Park Medical Centre",
    caption:
      "Four technicians on separate rope lines, cleaning a bay of glazing in a single shift.",
    alt: "Four technicians on ropes cleaning the glazed façade of Farrer Park Medical Centre",
    orientation: "portrait",
  },
  {
    id: "cleaning-universal-studios",
    src: require("@/assets/work/rope-cleaning-universal-studios.jpg"),
    category: ROPE_ACCESS,
    service: "Façade cleaning, night works",
    location: "Universal Studios Singapore",
    caption:
      "Night works scheduled outside opening hours to keep the attraction clear during the day.",
    alt: "A line of rope access technicians cleaning a painted themed façade at night",
    orientation: "portrait",
  },
  {
    id: "glazed-shaft-cleaning",
    src: require("@/assets/BluespeedAsset_2.jpeg"),
    category: ROPE_ACCESS,
    service: "Façade cleaning",
    caption:
      "Cleaning a narrow glazed light shaft between structural fins, from the top floor down.",
    alt: "A technician descending a narrow glazed shaft between white concrete columns",
    orientation: "portrait",
  },
  {
    id: "external-wall-repaint",
    src: require("@/assets/work/rope-external-wall-repaint.jpg"),
    category: ROPE_ACCESS,
    service: "External wall repainting",
    caption:
      "Making good and repainting an external wall above an entrance that stayed open to the public.",
    alt: "Two rope access technicians preparing and repainting a grey external wall",
    orientation: "portrait",
  },
  {
    id: "brick-wall-painting",
    src: require("@/assets/work/rope-brick-wall-painting.jpg"),
    category: ROPE_ACCESS,
    service: "Painting works",
    caption:
      "Treating a brick-clad column inside an atrium, worked from independent rope systems.",
    alt: "Two technicians on ropes applying coating to a curved brick column inside an atrium",
    orientation: "portrait",
  },
  {
    id: "facade-plaster-repair",
    src: require("@/assets/work/rope-facade-plaster-repair.jpg"),
    category: ROPE_ACCESS,
    service: "Façade plaster wall repair",
    caption:
      "Plastering and making good a façade panel — full harness, helmet and a working and backup line.",
    alt: "Close-up of a harnessed technician skimming plaster onto a façade wall with a trowel",
    orientation: "portrait",
  },
  {
    id: "sealant-joint-repair",
    src: require("@/assets/work/rope-sealant-joint-repair.jpg"),
    category: ROPE_ACCESS,
    service: "Sealant installation and replacement",
    caption:
      "Following a vertical joint line down a stone and glass elevation, one drop at a time.",
    alt: "Overhead view of a technician on rope working a vertical joint on a green glazed façade",
    orientation: "portrait",
  },
  {
    id: "sealant-joint-repair-2",
    src: require("@/assets/work/rope-sealant-joint-repair-2.jpg"),
    category: ROPE_ACCESS,
    service: "Sealant installation and replacement",
    caption:
      "Joint works on a panelled elevation, positioned precisely against the panel grid.",
    alt: "A technician in a yellow helmet working on the joints of a green and gold panelled façade",
    orientation: "portrait",
  },
  {
    id: "sealant-joint-replacement",
    src: require("@/assets/work/rope-sealant-joint-replacement.jpg"),
    category: ROPE_ACCESS,
    service: "Sealant installation and replacement",
    caption:
      "Fresh sealant run into a taped and prepared skylight joint, then tooled off.",
    alt: "Close-up of a sealant gun applying black sealant to a masked skylight joint",
    orientation: "portrait",
  },

  /* ---------- Building and Civil Division ---------- */

  {
    id: "jet-wash-cleaning",
    src: require("@/assets/work/build-jet-wash-cleaning.jpg"),
    category: BUILDING_CIVIL,
    service: "Jet wash cleaning",
    caption:
      "High-pressure washing of external staircases and common areas as part of general maintenance.",
    alt: "A Blue Speed worker jet washing an external tiled staircase",
    orientation: "portrait",
  },
  {
    id: "external-painting",
    src: require("@/assets/work/build-external-painting.jpg"),
    category: BUILDING_CIVIL,
    service: "Internal and external painting works",
    caption:
      "Repainting external walls and services enclosures, worked around live plant.",
    alt: "A Blue Speed worker rolling paint onto an external wall beside pipework",
    orientation: "portrait",
  },
  {
    id: "cleanroom-painting",
    src: require("@/assets/work/build-cleanroom-painting.jpg"),
    category: BUILDING_CIVIL,
    service: "Painting works in controlled environments",
    caption:
      "Internal repainting inside a controlled area, carried out under the site's own PPE and entry rules.",
    alt: "A worker in full cleanroom PPE rolling paint onto a wall in a controlled corridor",
    orientation: "portrait",
  },
  {
    id: "waterproofing-1",
    src: require("@/assets/work/build-waterproofing-1.jpg"),
    category: BUILDING_CIVIL,
    service: "Torch-on waterproofing",
    caption:
      "Torch-applied membrane laid across a flat roof and dressed up the upstand.",
    alt: "A flat roof finished with green torch-on waterproofing membrane",
    orientation: "landscape",
  },
  {
    id: "waterproofing-2",
    src: require("@/assets/work/build-waterproofing-2.jpg"),
    category: BUILDING_CIVIL,
    service: "Torch-on waterproofing",
    caption:
      "Membrane laid out across a large roof deck and worked around the existing services runs.",
    alt: "A large roof deck covered in green waterproofing membrane beside service pipework",
    orientation: "landscape",
  },
  {
    id: "spalling-inspection",
    src: require("@/assets/work/build-spalling-inspection.jpg"),
    category: BUILDING_CIVIL,
    service: "Concrete spalling repair",
    caption:
      "Step one — delaminated soffit identified and the extent marked out during inspection.",
    alt: "A damaged ceiling soffit with plaster broken away, exposing the concrete behind",
    orientation: "portrait",
  },
  {
    id: "spalling-rebar",
    src: require("@/assets/work/build-spalling-rebar.jpg"),
    category: BUILDING_CIVIL,
    service: "Concrete spalling repair",
    caption:
      "Step two — corroded reinforcement exposed along the beam line so the cause can be treated.",
    alt: "Corroded steel reinforcement exposed along a cracked concrete beam soffit",
    orientation: "portrait",
  },
  {
    id: "spalling-hacking",
    src: require("@/assets/work/build-spalling-hacking.jpg"),
    category: BUILDING_CIVIL,
    service: "Concrete spalling repair",
    caption:
      "Step three — hacking back to sound concrete before the bars are treated and the soffit made good.",
    alt: "A worker using a rotary hammer to hack back damaged concrete on a ceiling",
    orientation: "portrait",
  },
  {
    id: "tile-replacement",
    src: require("@/assets/work/build-tile-replacement.jpg"),
    category: BUILDING_CIVIL,
    service: "Renovation and reinstatement works",
    caption:
      "Tiling and reinstatement completed and handed back — the state a room is returned in.",
    alt: "A completed tiled bathroom with new sanitary fittings after reinstatement works",
    orientation: "portrait",
  },
  {
    id: "drain-grating-2",
    src: require("@/assets/work/build-drain-grating-2.jpg"),
    category: BUILDING_CIVIL,
    service: "Building and civil works",
    caption:
      "Replacement drain gratings set flush along a covered walkway used daily by the public.",
    alt: "New galvanised drain grating covers set flush into the floor of a covered walkway",
    orientation: "portrait",
  },
  {
    id: "drain-grating-1",
    src: require("@/assets/work/build-drain-grating-1.jpg"),
    category: BUILDING_CIVIL,
    service: "Building and civil works",
    caption:
      "New grating covers along a vehicle entrance, cleaned down and reopened the same day.",
    alt: "New metal drain grating covers running alongside a brick-paved vehicle entrance",
    orientation: "portrait",
  },
];

/** Look a photo up by id — used by views that want one specific image. */
export function photo(id) {
  const match = workPhotos.find((item) => item.id === id);

  if (!match) {
    throw new Error(`Unknown work photo id: "${id}"`);
  }

  return match;
}

/** All photos for one division, in registry order. */
export function photosFor(category) {
  return workPhotos.filter((item) => item.category === category);
}

/* ---------------------------------------------------------------
   Named selections used by the pages
--------------------------------------------------------------- */

/** Home page hero — one lead image plus two supporting shots. */
export const homeHero = {
  lead: photo("cleaning-genting-hotel"),
  support: [photo("cleaning-mbfc-tower-1"), photo("external-painting")],
};

/** Page hero images, keyed by route. */
export const pageHeroes = {
  about: photo("facade-cladding"),
  services: photo("limited-access-atrium"),
  qualitySafety: photo("facade-plaster-repair"),
  work: photo("roof-painting-marriott"),
};

/** Card images for the two divisions. */
export const divisionImages = {
  buildingCivil: photo("waterproofing-1"),
  ropeAccess: photo("painting-farrer-park"),
};

/** Sticky media on the Divisions page. */
export const divisionFeature = {
  buildingCivil: photo("jet-wash-cleaning"),
  ropeAccess: photo("cleaning-farrer-sunshade"),
};

/** Six-photo strip on the home page. */
export const featuredWork = [
  photo("cleaning-mbfc-tower-1"),
  photo("limited-access-atrium"),
  photo("roof-painting-marriott"),
  photo("facade-plaster-repair"),
  photo("waterproofing-2"),
  photo("tile-replacement"),
];

/** Four-photo strip on the Services page. */
export const serviceStrip = [
  photo("cleaning-farrer-glass"),
  photo("external-painting"),
  photo("sealant-joint-replacement"),
  photo("spalling-hacking"),
];
